/** Review (PRD §20–§21): watch, read the Quality Report, leave timestamped notes, triage others' notes, decide. */
import { useRef, useState } from "react";
import type { ReviewAction } from "@contentops/contract";
import type { z } from "zod";
import { Link, navigate } from "../../app/router";
import type { FeedbackInput } from "../../api/client";
import type { Feedback, VersionDetail } from "../../api/types";
import { Icon } from "../../components/Icon";
import { useToast } from "../../components/toast";
import { Badge, Button, Card, ConfirmDialog, EmptyState, QueryView } from "../../components/ui";
import { useApi, useMutation } from "../../data/hooks";
import { useVersion, useVersionCovers } from "../../data/queries";
import { clock, languageName, relative } from "../../lib/format";
import { DECISION_LABEL, VERSION_STATUS } from "../../lib/status";
import { DecisionCard } from "../studio/DecisionCard";
import { CoverPicker, coversFromVersion, defaultCover } from "./CoverPicker";
import { FeedbackComposer } from "./FeedbackComposer";
import { QualityReportCard } from "./QualityReportCard";
import { ShareSheet } from "./ShareSheet";

type Action = z.input<typeof ReviewAction>["action"];

export function ReviewScreen({ versionId }: { versionId: string }) {
  const q = useVersion(versionId);
  return <QueryView q={q}>{(d) => <ReviewBody d={d} />}</QueryView>;
}

const CONFIRM: Partial<Record<Action, { title: string; impact: string; label: string; tone: "danger" | "primary" }>> = {
  reject: { title: "Reject this episode?", impact: "The episode is dropped from the series. Any published “Next: …” teaser that promises it is flagged for a fix. This can't be undone from here.", label: "Reject episode", tone: "danger" },
  regenerate: { title: "Regenerate from scratch?", impact: "The Studio discards this approach and builds a fresh version with a different creative direction. Your notes on this version go with it.", label: "Regenerate", tone: "danger" },
  hold: { title: "Put this episode on hold?", impact: "The episode is parked (for example, awaiting a scholar). Nothing is built or published until you release it.", label: "Hold", tone: "primary" },
};

function ReviewBody({ d }: { d: VersionDetail }) {
  const api = useApi();
  const toast = useToast();
  const v = d.version;
  const videoRef = useRef<HTMLVideoElement>(null);
  const video = d.artifacts.find((a) => a.kind === "video_review") ?? d.artifacts.find((a) => a.kind === "video_master");
  const videoUrl = video ? api.artifactUrl(video.id) : "";
  // Covers come from GET …/covers (settable / problem); the version's own cover artifacts stand in until it answers.
  const coversQ = useVersionCovers(v.id);
  const coverData = coversQ.data ?? coversFromVersion(d);
  const [picked, setPicked] = useState<string | undefined>();
  const cover = defaultCover(coverData, picked);
  const [sendIds, setSendIds] = useState<string[]>(() => d.feedback.filter((f) => f.status === "new" && f.verdict !== "looks_good" && f.author_role === "owner").map((f) => f.id));
  const [confirm, setConfirm] = useState<Action | null>(null);
  const [sharing, setSharing] = useState(false);
  const status = VERSION_STATUS[v.status];
  const reviewable = v.status === "in_review" || v.status === "changes_requested";
  const keys = [`version/${v.id}`, `series/${v.series_id}`, `messages/${v.series_id}`, "series", "dashboard"];

  const seek = (t: number) => {
    const el = videoRef.current;
    if (!el) return;
    el.currentTime = t;
    void el.play?.()?.catch(() => undefined);
    el.scrollIntoView?.({ block: "center", behavior: "smooth" });
  };
  const addFb = useMutation((fb: FeedbackInput) => api.addFeedback(v.id, fb), { invalidate: [`version/${v.id}`] });
  const review = useMutation((a: z.input<typeof ReviewAction>) => api.reviewVersion(v.id, a), { invalidate: keys });

  const doAction = (action: Action) => {
    if (action === "request_changes" && !sendIds.length) { toast.show({ tone: "info", text: "Add a note (or tick one below) so the Studio knows what to change." }); return; }
    if (CONFIRM[action]) { setConfirm(action); return; }
    void run(action);
  };
  const run = async (action: Action) => {
    const body: z.input<typeof ReviewAction> = { action, feedback_ids: action === "request_changes" || action === "regenerate" ? sendIds : [], cover_artifact_id: action === "approve" ? cover : undefined };
    try {
      await review.mutateAsync(body);
      toast.ok({ approve: "Approved.", request_changes: "Sent to the Studio.", reject: "Rejected.", regenerate: "Regeneration requested.", hold: "On hold." }[action]);
    } catch (e) {
      toast.error(e, "Couldn't complete that");
    } finally {
      setConfirm(null);
    }
  };

  const onSubmitFeedback = async (fb: FeedbackInput) => {
    try {
      const created = await addFb.mutateAsync(fb);
      if (created.verdict !== "looks_good") setSendIds((ids) => [...ids, created.id]);
      return true;
    } catch (e) {
      toast.error(e, "Couldn't add the note");
      return false;
    }
  };

  return (
    <div className="review">
      <header className="review-head">
        <Link to={`/studio/${v.series_id}`} className="back-link"><Icon name="back" size={18} /> {v.series_title}</Link>
        <h1>Episode {v.episode_sequence}{v.episodes_total ? ` of ${v.episodes_total}` : ""} · {v.episode_title}</h1>
        <div className="series-head-meta">
          <Badge tone={status.tone}>{status.label}</Badge>
          <Badge tone="neutral">{languageName(v.language_code)} · {v.role}</Badge>
          {v.origin !== "studio" && <Badge tone="info">{v.origin.replace(/_/g, " ")}</Badge>}
          {d.history.length > 1 ? (
            <label className="version-switch">
              <span className="sr-only">Version</span>
              <select value={v.id} onChange={(e) => navigate(`/review/${e.target.value}`, { replace: true })}>
                {d.history.map((h) => <option key={h.id} value={h.id}>v{h.version} · {VERSION_STATUS[h.status].label}{h.score != null ? ` · ${h.score.toFixed(1)}` : ""}</option>)}
              </select>
            </label>
          ) : <Badge tone="neutral">v{v.version}</Badge>}
        </div>
      </header>

      <div className="review-grid">
        <div className="review-media">
          <div className="player">
            {videoUrl ? (
              <video ref={videoRef} src={videoUrl} controls playsInline preload="metadata" aria-label={`Episode ${v.episode_sequence} version ${v.version}`} />
            ) : (
              <div className="player-empty"><Icon name="play" size={36} /><span>{video ? "Preview unavailable in mock mode" : "No video artifact yet"}</span>{v.duration_s ? <span className="muted small">{clock(v.duration_s)}</span> : null}</div>
            )}
          </div>
          {v.changelog && <Card title="What changed"><p className="pre">{v.changelog}</p></Card>}
          {d.decisions.filter((x) => x.status === "open").map((x) => <DecisionCard key={x.id} decision={x} />)}
          {/* Awaiting review: the pick goes with Approve. Otherwise (approved included): Choose saves it and sets the YouTube thumbnail. */}
          {reviewable
            ? <CoverPicker versionId={v.id} fallback={coverData} selection={{ value: cover, onChange: setPicked }} hint="Your pick is saved when you approve; YouTube uses it as the thumbnail." />
            : <CoverPicker versionId={v.id} fallback={coverData} invalidate={["publication", "publications"]} hint="The chosen cover becomes the YouTube thumbnail of this episode's posts." />}
        </div>

        <div className="review-side">
          {v.quality_report ? <QualityReportCard report={v.quality_report} onSeek={seek} /> : <Card title="Quality report"><EmptyState icon="alert" title="No quality report">This version has no report — treat it as unvalidated.</EmptyState></Card>}

          <Card title="Notes">
            {reviewable && <FeedbackComposer getTime={() => videoRef.current?.currentTime ?? null} onCompose={() => videoRef.current?.pause?.()} onSubmit={onSubmitFeedback} busy={addFb.pending} />}
            <FeedbackList items={d.feedback} onSeek={seek} selectable={reviewable} selected={sendIds} onToggle={(id) => setSendIds((ids) => (ids.includes(id) ? ids.filter((x) => x !== id) : [...ids, id]))} />
          </Card>

          <Metadata metadata={v.metadata} />
        </div>
      </div>

      {v.quality_report?.speaker_review && <SpeakerReviewNote review={v.quality_report.speaker_review} />}
      {v.quality_report && v.quality_report.auto_decisions.length > 0 && <AutoDecisions items={v.quality_report.auto_decisions} />}

      <div className="action-bar" role="toolbar" aria-label="Review actions">
        {reviewable ? (
          <>
            <Button variant="primary" size="lg" icon="check" busy={review.pending && !confirm} onClick={() => doAction("approve")}>Approve</Button>
            <Button variant="secondary" size="lg" onClick={() => doAction("request_changes")}>Request changes{sendIds.length ? ` (${sendIds.length})` : ""}</Button>
            <details className="more-actions">
              <summary className="btn btn-ghost btn-lg" aria-label="More actions">More</summary>
              <div className="menu">
                <button type="button" onClick={() => setSharing(true)}><Icon name="share" size={18} /> Share for review</button>
                <button type="button" onClick={() => doAction("hold")}><Icon name="pause" size={18} /> Hold</button>
                <button type="button" onClick={() => doAction("regenerate")}><Icon name="refresh" size={18} /> Regenerate</button>
                <button type="button" className="danger-text" onClick={() => doAction("reject")}><Icon name="x" size={18} /> Reject</button>
              </div>
            </details>
          </>
        ) : v.status === "approved" ? (
          <>
            <Link to={`/publish/schedule?series=${encodeURIComponent(v.series_id)}&version=${encodeURIComponent(v.id)}`} className="btn btn-primary btn-lg"><Icon name="calendar" size={20} /><span>Schedule</span></Link>
            <Button variant="secondary" size="lg" icon="share" onClick={() => setSharing(true)}>Share</Button>
          </>
        ) : <p className="muted">This version is {status.label.toLowerCase()} — nothing to decide here.</p>}
      </div>

      {confirm && CONFIRM[confirm] && (
        <ConfirmDialog title={CONFIRM[confirm]!.title} impact={CONFIRM[confirm]!.impact} confirmLabel={CONFIRM[confirm]!.label} tone={CONFIRM[confirm]!.tone} busy={review.pending}
          onCancel={() => setConfirm(null)} onConfirm={() => void run(confirm)} />
      )}
      {sharing && <ShareSheet versionId={v.id} onClose={() => setSharing(false)} />}
    </div>
  );
}

/** The Studio's call on whether the speaker should see this before it goes out (QualityReport.speaker_review). The owner decides. */
export function SpeakerReviewNote({ review }: { review: NonNullable<NonNullable<VersionDetail["version"]["quality_report"]>["speaker_review"]> }) {
  if (!review.suggested) {
    return (
      <section className="speaker-review tone-ok" aria-label="Speaker review">
        <Icon name="check" />
        <div className="speaker-review-body"><b>Nothing needs the speaker.</b><span className="muted small">The Studio found nothing the speaker has to check — approve when you're happy.</span></div>
      </section>
    );
  }
  return (
    <section className="speaker-review tone-attention" aria-label="Speaker review">
      <Icon name="user" />
      <div className="speaker-review-body">
        <b>Speaker review suggested:</b>
        {review.points.length > 0 ? <ul>{review.points.map((p) => <li key={p}>{p}</li>)}</ul> : <span className="small">The Studio suggests the speaker looks at this before it goes out.</span>}
        <span className="muted small">Your call: approve directly, or have the speaker look first.</span>
      </div>
    </section>
  );
}

type QR = NonNullable<VersionDetail["version"]["quality_report"]>;
/** What the Studio decided on its own (autonomy.decisions = auto) — shown before the owner approves, so he can overrule. */
export function AutoDecisions({ items }: { items: QR["auto_decisions"] }) {
  return (
    <section className="auto-decided" aria-labelledby="auto-decided-h">
      <h2 id="auto-decided-h" className="card-title">Decided without you <span className="count-pill">{items.length}</span></h2>
      <ul>
        {items.map((a, i) => (
          <li key={`${i}-${a.question}`}>
            <span className="auto-q">{a.question}</span>
            <span className="auto-a"><Icon name="check" size={16} /> {a.chosen}</span>
            <span className="muted small">{a.why} · {DECISION_LABEL[a.type] ?? a.type.replace(/_/g, " ")}</span>
          </li>
        ))}
      </ul>
      <p className="muted small">Disagree with one? Add a note and request changes.</p>
    </section>
  );
}

const ROLE_TONE = { owner: "accent", reviewer: "info", guest: "neutral" } as const;
const VERDICT_LABEL: Record<Feedback["verdict"], string> = { fix: "Fix", comment: "Comment", regenerate: "Regenerate", hold: "Hold", looks_good: "Looks good ✓" };

export function FeedbackList({ items, onSeek, selectable, selected, onToggle }: { items: Feedback[]; onSeek: (t: number) => void; selectable: boolean; selected: string[]; onToggle: (id: string) => void }) {
  if (!items.length) return <p className="muted small">No notes yet. Notes from reviewers and guests appear here, attributed.</p>;
  const sorted = [...items].sort((a, b) => (a.at_seconds ?? 1e9) - (b.at_seconds ?? 1e9));
  return (
    <ul className="feedback-list">
      {sorted.map((f) => {
        const canSend = selectable && f.status === "new" && f.verdict !== "looks_good";
        return (
          <li key={f.id} className={`fb fb-${f.verdict}`}>
            <div className="fb-head">
              <Badge tone={ROLE_TONE[f.author_role]}>{f.author}</Badge>
              <span className={`fb-verdict ${f.verdict === "looks_good" ? "ok-text" : ""}`}>{VERDICT_LABEL[f.verdict]}</span>
              {f.at_seconds != null && <button type="button" className="time-link" onClick={() => onSeek(f.at_seconds!)} aria-label={`Play from ${clock(f.at_seconds)}`}>{clock(f.at_seconds)}</button>}
              <span className="muted small push">{relative(f.created_at)}</span>
            </div>
            {f.note && <p className="fb-note">{f.note}</p>}
            <div className="fb-foot">
              {f.status === "sent_to_studio" && <span className="muted small"><Icon name="check" size={14} /> Sent to the Studio</span>}
              {canSend && (
                <label className="check-row small">
                  <input type="checkbox" checked={selected.includes(f.id)} onChange={() => onToggle(f.id)} /> Send to the Studio with “Request changes”
                </label>
              )}
              {f.scope !== "version" && <Badge tone="info">{f.scope.replace(/_/g, " ")}</Badge>}
            </div>
          </li>
        );
      })}
    </ul>
  );
}

function Metadata({ metadata }: { metadata: Record<string, unknown> }) {
  const entries = Object.entries(metadata);
  if (!entries.length) return null;
  return (
    <Card title="Titles, captions and tags">
      <dl className="meta-list">
        {entries.map(([platform, value]) => (
          <div key={platform}>
            <dt>{platform.replace(/_/g, " ")}</dt>
            <dd>{typeof value === "string" ? value : value && typeof value === "object" ? (
              <dl className="meta-inner">
                {Object.entries(value as Record<string, unknown>).map(([k, x]) => <div key={k}><dt>{k}</dt><dd>{Array.isArray(x) ? x.join(", ") : String(x)}</dd></div>)}
              </dl>
            ) : String(value)}</dd>
          </div>
        ))}
      </dl>
    </Card>
  );
}
